import React, { useEffect, useState } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  TrendingUp, Download, DollarSign, Target, Globe, AlertTriangle,
  Users, TrendingDown, Info, ChevronDown, ChevronUp
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import api from '@/services/api';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useBranding } from '@/context/BrandingContext';
import { createBrandedPDF, addSectionTitle, addMetricCards, addTable, downloadPDF } from '@/utils/pdfExport';
import DateRangeSelect from '@/components/DateRangeSelect';
import SiteDeptFilter from '@/components/SiteDeptFilter';

const SavingsIntelligence = () => {
  const { t } = useTranslation();
  const [savingsData, setSavingsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState({});
  const { branding } = useBranding();

  useEffect(() => {
    fetchSavingsData();
  }, []);

  const fetchSavingsData = async () => {
    try {
      const response = await api.get('/savings/overview');
      setSavingsData(response.data);
    } catch (error) {
      toast.error('Failed to load savings data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  if (loading) {
    return (
      <Layout title="Savings Intelligence" subtitle="Comprehensive value analysis">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        </div>
      </Layout>
    );
  }

  const savingsByType = savingsData?.savings_by_type || {};
  const weeklyTrend = savingsData?.weekly_trend || {};
  const totalSavings = savingsData?.total_savings || 0;
  const avgConfidence = savingsData?.average_confidence || 0;

  // Chart colors
  const COLORS = ['#16a34a', '#0ea5e9', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

  // Prepare data for charts
  const savingsTypeData = Object.entries(savingsByType).map(([type, amount]) => ({
    name: type.replace('_', ' ').toUpperCase(),
    value: amount,
    displayName: type.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }));

  const weeklyTrendData = Object.entries(weeklyTrend).map(([week, amount]) => ({
    week: `Week ${week}`,
    savings: amount
  }));

  const savingsCards = [
    {
      id: 'max',
      title: 'Maximum Savings',
      amount: savingsByType.max || 0,
      icon: TrendingUp,
      color: 'green',
      calculation: 'Difference between your current price and the absolute lowest market price available',
      formula: 'Max Savings = Σ(Your Price - Lowest Market Price) × Quantity'
    },
    {
      id: 'market_average',
      title: 'Market Average Savings',
      amount: savingsByType.market_average || 0,
      icon: Target,
      color: 'blue',
      calculation: 'Savings when compared to peer-normalized and industry-average pricing',
      formula: 'Market Avg Savings = Σ(Your Price - Industry Avg Price) × Quantity'
    },
    {
      id: 'fff_alternate',
      title: 'FFF Alternate Savings',
      amount: savingsByType.fff_alternate || 0,
      icon: Target,
      color: 'purple',
      calculation: 'Cost reduction through Form-Fit-Function exact match alternates (pin-to-pin compatible)',
      formula: 'FFF Savings = Σ(Original Price - Alternate Price) × Quantity'
    },
    {
      id: 'price_prediction',
      title: 'Price Prediction Value',
      amount: savingsByType.price_prediction || 0,
      icon: TrendingDown,
      color: 'orange',
      calculation: 'Value from acting on AI price predictions (increases avoided + decreases captured)',
      formula: 'Prediction Value = Σ(Predicted Increase × Quantity) for timely purchases'
    },
    {
      id: 'global_sourcing',
      title: 'Global Sourcing Savings',
      amount: savingsByType.global_sourcing || 0,
      icon: Globe,
      color: 'cyan',
      calculation: 'Savings from de-risking through geographic diversification and global distributor network',
      formula: 'Global Savings = Cost reduction through multi-country sourcing strategy'
    },
    {
      id: 'risk_identified',
      title: 'Risk Impact Mitigation',
      amount: savingsByType.risk_identified || 0,
      icon: AlertTriangle,
      color: 'red',
      calculation: 'Financial impact of risks identified (price volatility, supply chain disruption, obsolescence)',
      formula: 'Risk Value = Σ(Potential Loss × Probability) for each identified risk'
    },
    {
      id: 'client_submitted',
      title: 'Your Wins (Client Actions)',
      amount: savingsByType.client_submitted || 0,
      icon: Users,
      color: 'pink',
      calculation: 'Savings you achieved through renegotiation, alternate selection, and timely action',
      formula: 'Your Wins = Self-reported savings from actions taken based on our insights'
    },
    {
      id: 'india_manufacturing',
      title: 'India Manufacturing Savings',
      amount: savingsByType.india_manufacturing || 0,
      icon: DollarSign,
      color: 'lime',
      calculation: 'Cost benefits from India-manufactured component sourcing options',
      formula: 'India Savings = Σ(Import Price - India Mfg Price) × Quantity'
    }
  ];

  const colorMap = {
    green: 'bg-green-600',
    blue: 'bg-blue-600',
    purple: 'bg-purple-600',
    orange: 'bg-orange-600',
    cyan: 'bg-cyan-600',
    red: 'bg-red-600',
    pink: 'bg-pink-600',
    lime: 'bg-lime-600'
  };

  return (
    <Layout title="Savings Intelligence" subtitle="Value Unlocked - Comprehensive savings analysis with AI insights">
      <div className="space-y-6" data-testid="savings-intelligence-page">
        {/* Filters */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-3 pb-3">
            <div className="flex gap-3 flex-wrap items-center">
              <DateRangeSelect value="12m" onChange={() => {}} testId="savings-intel-date-range" />
              <SiteDeptFilter site="All Sites" onSiteChange={() => {}} dept="All Departments" onDeptChange={() => {}} />
            </div>
          </CardContent>
        </Card>

        {/* Total Savings Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 shadow-sm col-span-2">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium mb-2">Total Savings Till Date</p>
                  <p className="text-5xl font-bold text-green-700">
                    ${totalSavings.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-2">Across {savingsData?.savings_count || 0} savings opportunities</p>
                </div>
                <TrendingUp className="h-20 w-20 text-green-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 shadow-sm">
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600 font-medium mb-2">Average Trust Score</p>
              <p className="text-5xl font-bold text-blue-700">{avgConfidence}%</p>
              <TooltipProvider>
                <UITooltip>
                  <TooltipTrigger>
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                      <Info className="h-4 w-4" />
                      <span>How is this calculated?</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-900 text-white max-w-sm">
                    <p className="font-semibold mb-2">Trust Score Calculation:</p>
                    <p className="text-xs">• Data Points Available (30%)</p>
                    <p className="text-xs">• Industry Match (25%)</p>
                    <p className="text-xs">• Quantity Similarity (20%)</p>
                    <p className="text-xs">• Component Type (15%)</p>
                    <p className="text-xs">• Data Recency (10%)</p>
                  </TooltipContent>
                </UITooltip>
              </TooltipProvider>
            </CardContent>
          </Card>
        </div>

        {/* 8 Savings Types - Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {savingsCards.map((card) => {
            const Icon = card.icon;
            const isExpanded = expandedCards[card.id];
            return (
              <Card key={card.id} className="bg-white border-gray-200 shadow-sm hover:shadow-sm transition-all">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className={`p-2 rounded-lg ${colorMap[card.color]} bg-opacity-10`}>
                      <Icon className={`h-5 w-5 text-${card.color}-600`} />
                    </div>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={() => toggleCardExpansion(card.id)}
                          >
                            <Info className="h-4 w-4 text-gray-400" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-gray-900 text-white max-w-xs">
                          <p className="text-xs font-semibold mb-1">Click for calculation details</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  <CardTitle className="text-sm text-gray-600 mt-2">{card.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-gray-900">
                    ${card.amount.toLocaleString()}
                  </p>
                  
                  <Collapsible open={isExpanded}>
                    <CollapsibleTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="w-full mt-3 text-xs text-gray-600 hover:text-green-600 flex items-center justify-center gap-1"
                        onClick={() => toggleCardExpansion(card.id)}
                      >
                        {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {isExpanded ? 'Hide' : 'Show'} Calculation
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-700 mb-2">{card.calculation}</p>
                      <div className="bg-white p-2 rounded border border-gray-300 mt-2">
                        <p className="text-xs font-mono text-gray-600">{card.formula}</p>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Charts Section */}
        <Tabs defaultValue="trend" className="w-full">
          <TabsList className="bg-gray-100 grid w-full grid-cols-4">
            <TabsTrigger value="trend" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              Trend Analysis
            </TabsTrigger>
            <TabsTrigger value="comparison" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              Type Comparison
            </TabsTrigger>
            <TabsTrigger value="distribution" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              Distribution
            </TabsTrigger>
            <TabsTrigger value="cumulative" className="data-[state=active]:bg-green-600 data-[state=active]:text-white">
              Cumulative Value
            </TabsTrigger>
          </TabsList>

          {/* Line Chart - Trend */}
          <TabsContent value="trend" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-gray-900">Savings Trend Over Time</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">Weekly savings progression (Last 90 days)</p>
                  </div>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Info className="h-4 w-4 text-gray-400" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-900 text-white max-w-sm">
                        <p className="font-semibold mb-2">How this chart works:</p>
                        <p className="text-xs">Shows weekly aggregated savings from all sources.</p>
                        <p className="text-xs mt-1">Higher peaks indicate weeks with significant cost reduction opportunities identified.</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={weeklyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="week" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                      formatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="savings" 
                      stroke="#16a34a" 
                      strokeWidth={3}
                      dot={{ fill: '#16a34a', r: 5 }}
                      activeDot={{ r: 7 }}
                      name="Weekly Savings"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bar Chart - Comparison */}
          <TabsContent value="comparison" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-gray-900">Savings by Type Comparison</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">Compare different savings sources</p>
                  </div>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Info className="h-4 w-4 text-gray-400" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-900 text-white max-w-sm">
                        <p className="font-semibold mb-2">Reading this chart:</p>
                        <p className="text-xs">Each bar represents total savings from a specific optimization strategy.</p>
                        <p className="text-xs mt-1">Taller bars indicate more effective savings sources for your portfolio.</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={savingsTypeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="displayName" stroke="#6b7280" angle={-15} textAnchor="end" height={80} />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                      formatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Legend />
                    <Bar dataKey="value" name="Savings Amount" radius={[8, 8, 0, 0]}>
                      {savingsTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pie Chart - Distribution */}
          <TabsContent value="distribution" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-gray-900">Savings Distribution</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">Percentage breakdown by savings type</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-gray-300 text-gray-700"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Chart
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={savingsTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({displayName, percent}) => `${displayName}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {savingsTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
                
                {/* Legend with calculation info */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {savingsTypeData.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                      <span className="text-xs text-gray-600">{item.displayName}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Area Chart - Cumulative */}
          <TabsContent value="cumulative" className="mt-6">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-gray-900">Cumulative Savings Growth</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">How your total savings accumulated over time</p>
                  </div>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Info className="h-4 w-4 text-gray-400" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-900 text-white max-w-sm">
                        <p className="font-semibold mb-2">Cumulative View:</p>
                        <p className="text-xs">Shows running total of savings identified.</p>
                        <p className="text-xs mt-1">Steeper slope = faster value discovery rate.</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={weeklyTrendData.map((item, idx, arr) => ({
                    ...item,
                    cumulative: arr.slice(0, idx + 1).reduce((sum, d) => sum + d.savings, 0)
                  }))}>
                    <defs>
                      <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#16a34a" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#16a34a" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="week" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                      formatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="cumulative" 
                      stroke="#16a34a" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorSavings)"
                      name="Total Savings"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Export Section */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Download Comprehensive Report</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Export all savings data with calculations, charts, and detailed breakdowns
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => {
                  const doc = createBrandedPDF('Savings Intelligence', 'Comprehensive Savings Report', branding);
                  let y = doc._startY;
                  y = addSectionTitle(doc, 'Savings Overview', y);
                  if (savingsData) {
                    const o = savingsData.overview || {};
                    y = addMetricCards(doc, [
                      { label: 'Total Savings', value: `$${(o.total_potential_savings || 0).toLocaleString()}` },
                      { label: 'Components', value: String(o.total_components_analyzed || 0) },
                      { label: 'Categories', value: String(o.savings_categories || 0) },
                      { label: 'Trust Score', value: `${o.trust_score || 0}%` },
                    ], y);
                    if (savingsData.categories?.length) {
                      y = addSectionTitle(doc, 'Savings by Category', y);
                      y = addTable(doc, ['Category', 'Savings', 'Components', 'Trust'],
                        savingsData.categories.map(c => [c.name, `$${c.total_savings?.toLocaleString() || 0}`, String(c.component_count || 0), `${c.trust_score || 0}%`]), y);
                    }
                  }
                  downloadPDF(doc, 'savings-intelligence-report.pdf');
                  toast.success('PDF downloaded');
                }} className="bg-green-600 hover:bg-green-700 text-white" data-testid="savings-export-pdf">
                  <Download className="h-4 w-4 mr-2" />
                  Export PDF
                </Button>
                <Button variant="outline" className="border-gray-300 text-gray-700">
                  <Download className="h-4 w-4 mr-2" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SavingsIntelligence;